import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslationService } from 'src/app/services/translation.service';
import { environment } from 'src/environments/environment';
import * as BuildingActions from '../actions/building.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class BuildingEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private errorService: ErrorService,
    private store: Store<AppState>,
    private toastService: ToastService,
    private navController: NavController,
    private translationService: TranslationService
  ) { }
  createBuilding$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BuildingActions.createBuilding),
      switchMap((data) => {
        this.store.dispatch(BuildingActions.buildingLoading({ payload: { loading: true } }));
        return this.http.post<{ id: number }>(environment.apiUrl + '/buildings', data.payload).pipe(
          tap((response) => {
            this.navController.navigateBack('/buildings/' + response.id);
            this.toastService.showToast(this.translationService.translate('STATUS_UPDATE.UPDATE'));
          }),
          map(response => BuildingActions.buildingCreated({ id: response.id })),
          catchError((error) => {
            this.store.dispatch(BuildingActions.buildingLoading({ payload: { loading: false } }));
            return of(BuildingActions.buildingActionFail({
              payload: { error: this.errorService.mapError(error) }
            }));
          })
        );
      })
    );
  });

  getBuildingById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BuildingActions.readBuildingById),
      switchMap(({ payload }) => {
        BuildingActions.buildingLoading({ payload: { loading: true } });

        return (
          this.http
            // todo change any
            .get<any>(environment.apiUrl + '/buildings/' + payload.building_id)
            .pipe(
              mergeMap((response) => {
                return of(
                  BuildingActions.buildingLoading({
                    payload: { loading: false },
                  }),
                  BuildingActions.buildingByIdRead({
                    payload: {
                      building: response.data,
                    },
                  })
                );
              }),
              catchError((error) => {
                return of(
                  BuildingActions.buildingActionFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              })
            )
        );
      })
    )
  );

  updateBuilding$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BuildingActions.updateBuilding),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        return this.http
          .put<null>(
            environment.apiUrl + `/buildings/${payload.building_spatial.id}`,
            payload
          )
          .pipe(
            switchMap(() => {
              if (localStorage.getItem('isEditModeE9Entos') !== 'true') {
                this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.UPDATE')}`);
              }

              if (localStorage.getItem('isEditModeE9Ektos') !== 'true') {
                this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.UPDATE')}`);
              }
              return of(
                BuildingActions.buildingUpdated(),
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              return of(
                BuildingActions.buildingActionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
              // }
            })
          );
      })
    );
  });

  retiredOrReactivatedBuilding$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BuildingActions.retiredOrReactivatedBuilding),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BuildingActions.buildingLoading({ payload: { loading: true } })
        );
        let retireOrReact: string;
        if (payload.retired) {
          retireOrReact = 'retire';
        }
        else {
          retireOrReact = 'reactivation';
        }
        return this.http
          .put<null>(
            environment.apiUrl + `/props/${retireOrReact}/${payload.kadik}`,
            payload
          )
          .pipe(
            switchMap(() => {
              return of(
                BuildingActions.retiredOrReactivatedBuildingSuccess(),
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  BuildingActions.buildingOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else
                return of(
                  BuildingActions.buildingActionFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
            })
          );
      })
    );
  });

  deleteParcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BuildingActions.deleteBuilding),
      switchMap((payload) => {
        BuildingActions.buildingLoading({ payload: { loading: true } });
        return this.http

          .delete<any>(environment.apiUrl + '/buildings/' + payload.payload.id)
          .pipe(
            map((x) => {
              return x.data;
            }),
            switchMap((responseData) => {
              this.store.dispatch(
                BuildingActions.deleteBuildingSuccess({
                  payload: {
                    parcel: responseData,
                  },
                })
              );
              this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.DELETE')}`);
              return of(
                BuildingActions.buildingLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              this.store.dispatch(BuildingActions.buildingLoading({ payload: { loading: false } }));
              return of(
                BuildingActions.buildingActionFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BuildingActions.downloadFile),
      switchMap((data) => {
        this.store.dispatch(
          BuildingActions.fileLoading({
            payload: { loading: true },
          })
        );
        return this.http.get(`${environment.apiUrl}/files/${data.payload.filename}`,
          {
            params: {
              filename: data?.payload?.filename?.toString(),
              filepath: data?.payload?.filepath?.toString()
            },
            responseType: 'blob',
          }
        )
          .pipe(
            switchMap((blob) => {
              this.store.dispatch(BuildingActions.downloadFileSuccess());
              const fileURL = window.URL.createObjectURL(blob);
              window.open(fileURL);
              this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.DOWNLOAD')}`);
              return of(
                BuildingActions.fileLoading({
                  payload: { loading: false },
                })
              );
            }),
            catchError((error) => {
              this.store.dispatch(
                BuildingActions.fileLoading({ payload: { loading: false } })
              );
              if (error.error instanceof Blob) {
                return of(
                  BuildingActions.fileErrorMsgFromBlobResponse({
                    payload: { errorMsgBlob: error.error },
                  })
                );
              } else {
                return of(
                  BuildingActions.fileFail({
                    payload: { fileError: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  getBuildingsWithExpiringRights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BuildingActions.getBuildingsWithExpiringRights),
      switchMap(action => {
        return this.http
          .get<any[]>(`${environment.apiUrl}/buildingsWithExpiring`, {
            params: {
              startDate: action.payload.startDate,
              endDate: action.payload.endDate
            }
          })
          .pipe(
            map(responseData => {
              return BuildingActions.getBuildingsWithExpiringRightsSuccess({
                payload: { buildings: responseData }
              });
            }),
            catchError(error => {
              return of(BuildingActions.buildingActionFail({
                payload: { error: this.errorService.mapError(error) }
              }));
            }),
          );
      })
    )
  );
}
